import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AuthContext } from '../App';

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VODPage() {
  const { username, vodId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const chatRef = useRef(null);

  const [vod, setVod] = useState(null);
  const [loading, setLoading] = useState(true);

  // Clip dialog
  const [showClip, setShowClip] = useState(false);
  const [clipTitle, setClipTitle] = useState('');
  const [clipStart, setClipStart] = useState(0);
  const [clipDuration, setClipDuration] = useState(30);
  const [clipping, setClipping] = useState(false);
  const [clipStatus, setClipStatus] = useState('');

  // Chat replay
  const [chatMessages, setChatMessages] = useState([]);
  const [visibleChat, setVisibleChat] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);

  // Mute tool
  const [showMuteTool, setShowMuteTool] = useState(false);
  const [muteStart, setMuteStart] = useState(0);
  const [muteEnd, setMuteEnd] = useState(30);
  const [muteReason, setMuteReason] = useState('Copyrighted music');
  const [muteStatus, setMuteStatus] = useState('');
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    api.vod(vodId)
      .then(setVod)
      .catch(() => navigate(`/${username}`))
      .finally(() => setLoading(false));
    api.vodChat(vodId).then(setChatMessages).catch(() => {});
  }, [vodId]);

  // Sync chat with video time + enforce muted sections
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      const t = video.currentTime;
      setCurrentTime(t);
      // Check if current time is in a muted section
      const muted = vod?.muted_sections?.some(s => t >= s.start_seconds && t <= s.end_seconds);
      if (muted && !video.muted) {
        video.muted = true;
        setIsMuted(true);
      } else if (!muted && isMuted) {
        video.muted = false;
        setIsMuted(false);
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [vod]);

  useEffect(() => {
    const visible = chatMessages.filter(m => m.offset_seconds != null && m.offset_seconds <= currentTime);
    setVisibleChat(visible.slice(-100));
  }, [currentTime, chatMessages]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleChat]);

  const openClipDialog = () => {
    const ct = videoRef.current?.currentTime || 0;
    const start = Math.max(0, ct - 30);
    setClipStart(Math.floor(start));
    setClipDuration(Math.min(30, Math.floor(ct - start) || 30));
    setClipTitle(`${vod?.title || 'Clip'}`);
    setClipStatus('');
    setShowClip(true);
  };

  const handleCreateClip = async () => {
    if (!clipTitle.trim()) return;
    setClipping(true);
    setClipStatus('');
    try {
      const res = await api.createClip({
        stream_id: parseInt(vodId),
        title: clipTitle.trim(),
        start_time: clipStart,
        duration: clipDuration,
      });
      setClipStatus(`Clip is processing! View it at ninty.live/clips/${res.clip_id} in ~1 min.`);
      setClipping(false);
    } catch (e) {
      setClipStatus(`Error: ${e.message}`);
      setClipping(false);
    }
  };

  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!vod) return null;

  const maxDuration = Math.min(60, vod.vod_duration ? Math.floor(vod.vod_duration - clipStart) : 60);

  return (
    <div className="vod-layout">
      {/* Left: video + info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="video-player" style={{ borderRadius: 12, marginBottom: 16 }}>
          <video
            ref={videoRef}
            src={`https://stream.mux.com/${vod.vod_playback_id}.m3u8`}
            controls
            autoPlay
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 }}>{vod.title}</h1>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', color: '#adadb8', fontSize: '0.85rem' }}>
              <button onClick={() => navigate(`/${username}`)}
                style={{ color: '#D6E9AA', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}>
                {vod.display_name || username}
              </button>
              <span style={{ textTransform: 'capitalize' }}>{vod.category}</span>
              {vod.vod_duration != null && <span>{formatDuration(vod.vod_duration)}</span>}
              {vod.started_at && <span>{new Date(vod.started_at).toLocaleDateString()}</span>}
            </div>
          </div>
          {user && (
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={openClipDialog}>Clip</button>
              {(user.username === username || user.is_admin) && (
                <button className="btn btn-outline" onClick={() => { setShowMuteTool(!showMuteTool); setMuteStart(Math.floor(currentTime)); setMuteEnd(Math.floor(currentTime) + 30); }}>
                  Mute Section
                </button>
              )}
            </div>
          )}
        </div>

        {/* Muted section indicator */}
        {isMuted && (
          <div style={{ padding: '8px 14px', background: '#eb040022', border: '1px solid #eb040044', borderRadius: '2px', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', color: '#eb0400' }}>MUTED</span>
            <span style={{ fontSize: '0.82rem', color: '#adadb8' }}>Audio muted — copyrighted content detected in this section</span>
          </div>
        )}

        {/* Muted sections list */}
        {vod?.muted_sections?.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 700, marginBottom: '6px' }}>Muted Sections</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {vod.muted_sections.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: '#0e0e10', borderRadius: '2px', fontSize: '0.78rem', border: '1px solid #2a2a2e' }}>
                  <span style={{ color: '#eb0400' }}>{formatDuration(s.start_seconds)} — {formatDuration(s.end_seconds)}</span>
                  <span style={{ color: '#666' }}>{s.reason}</span>
                  {(user?.username === username || user?.is_admin) && (
                    <button onClick={async () => {
                      await api.removeVodMute(vodId, s.id);
                      setVod(prev => ({ ...prev, muted_sections: prev.muted_sections.filter(m => m.id !== s.id) }));
                    }} style={{ background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '0.75rem', padding: '0 2px' }}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mute tool */}
        {showMuteTool && (
          <div style={{ marginTop: '12px', padding: '16px', background: '#18181b', borderRadius: '2px', border: '1px solid #2a2a2e', boxShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '0.9rem' }}>Mute Audio Section</div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ fontSize: '0.75rem', color: '#adadb8', display: 'block', marginBottom: '4px' }}>Start ({formatDuration(muteStart)})</label>
                <input className="input" type="number" min="0" max={vod?.vod_duration || 9999} value={muteStart}
                  onChange={e => setMuteStart(Math.max(0, parseInt(e.target.value) || 0))} style={{ marginBottom: 0 }} />
              </div>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ fontSize: '0.75rem', color: '#adadb8', display: 'block', marginBottom: '4px' }}>End ({formatDuration(muteEnd)})</label>
                <input className="input" type="number" min="0" max={vod?.vod_duration || 9999} value={muteEnd}
                  onChange={e => setMuteEnd(Math.max(0, parseInt(e.target.value) || 0))} style={{ marginBottom: 0 }} />
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '0.75rem', color: '#adadb8', display: 'block', marginBottom: '4px' }}>Reason</label>
              <select className="input" value={muteReason} onChange={e => setMuteReason(e.target.value)} style={{ marginBottom: 0 }}>
                <option>Copyrighted music</option>
                <option>Copyrighted video</option>
                <option>DMCA claim</option>
                <option>Other</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button className="btn btn-primary" onClick={async () => {
                try {
                  const res = await api.addVodMute(vodId, { start_seconds: muteStart, end_seconds: muteEnd, reason: muteReason });
                  setVod(prev => ({ ...prev, muted_sections: [...(prev.muted_sections || []), res] }));
                  setMuteStatus('Section muted!');
                  setShowMuteTool(false);
                } catch (e) { setMuteStatus(e.message); }
                setTimeout(() => setMuteStatus(''), 3000);
              }}>Mute This Section</button>
              <button className="btn btn-secondary" onClick={() => setShowMuteTool(false)}>Cancel</button>
            </div>
            {muteStatus && <p style={{ fontSize: '0.82rem', marginTop: '8px', color: muteStatus.includes('muted') ? '#00c400' : '#eb0400' }}>{muteStatus}</p>}
          </div>
        )}

        {/* Copyright disclaimer */}
        <div style={{ marginTop: '12px', fontSize: '0.72rem', color: '#666' }}>
          Sections with copyrighted content may be muted. <a href="/copyright" style={{ color: 'var(--accent)' }}>Learn more</a>
        </div>
      </div>

      {/* Right: chat replay */}
      <div className="vod-chat-panel">
        <div className="chat-container" style={{ height: '100%' }}>
          <div className="chat-header">
            <span className="panel-title" style={{ margin: 0 }}>Chat Replay</span>
            <span style={{ fontSize: '0.75rem', color: '#adadb8' }}>
              {chatMessages.length} messages
            </span>
          </div>
          <div className="chat-messages" ref={chatRef}>
            {visibleChat.length === 0 && (
              <div className="chat-empty">
                {chatMessages.length === 0 ? 'No chat messages recorded' : 'Chat will appear as you watch...'}
              </div>
            )}
            {visibleChat.map((msg, i) => (
              <div key={i} className="chat-message">
                <span style={{ fontSize: '0.7rem', color: '#666', marginRight: '6px' }}>
                  {formatDuration(Math.max(0, msg.offset_seconds))}
                </span>
                <span className="chat-username verified">{msg.username}</span>
                <span className="chat-text">{msg.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Clip dialog */}
      {showClip && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#18181b', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Create Clip</h2>
              <button onClick={() => setShowClip(false)} style={{ background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
            </div>

            <div className="form-group">
              <label>Clip Title</label>
              <input className="input" value={clipTitle} onChange={e => setClipTitle(e.target.value)}
                placeholder="Give your clip a title..." style={{ marginBottom: 0 }} />
            </div>

            <div className="form-group" style={{ marginTop: 14 }}>
              <label>Start Time (seconds into VOD)</label>
              <input className="input" type="number" min="0"
                max={vod.vod_duration ? Math.floor(vod.vod_duration - 5) : 9999}
                value={clipStart}
                onChange={e => setClipStart(Math.max(0, parseInt(e.target.value) || 0))}
                style={{ marginBottom: 0 }} />
              <div style={{ fontSize: '0.75rem', color: '#adadb8', marginTop: 4 }}>
                {formatDuration(clipStart)} into the VOD
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 14 }}>
              <label>Duration: {clipDuration}s</label>
              <input type="range" min="5" max={maxDuration} value={clipDuration}
                onChange={e => setClipDuration(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: '#D6E9AA' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#adadb8' }}>
                <span>5s</span><span>{maxDuration}s (max 60s)</span>
              </div>
            </div>

            {clipStatus && (
              <p style={{ fontSize: '0.85rem', margin: '12px 0', color: clipStatus.startsWith('Error') ? '#eb0400' : '#00c400' }}>
                {clipStatus}
              </p>
            )}

            {!clipStatus.startsWith('Clip is') && (
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }}
                onClick={handleCreateClip} disabled={clipping || !clipTitle.trim()}>
                {clipping ? 'Creating...' : 'Create Clip'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
